import { getAppConfig } from '@bts-web/core-app-config';
import { FC, useState } from 'react';
import { TwoFaSectionTitle } from '../../../shared/TwoFaSectionTitle';
import { TwoFaSectionDescription } from '../../../shared/TwoFaSectionDescription';
import { TwoFaSectionButtonsWrapper } from '../../../shared/TwoFaSectionButtonsWrapper';
import { css } from '@bts-web/utils-style-engine';
import { HyperLinkButtonBase } from '../../../../../HyperLinkButtonBase/HyperLinkButtonBase';
import { ButtonBase } from '../../../../../ButtonBase/ButtonBase';
import { DeleteTwoFaOnContinue, ICodeResponseType } from '../../utils/types';
import { submitResetTwoFaAppCode } from '../../../../../../../auth';
import { useHandleGqlErrorsWithSnackbar } from '../../../../../../utils/errors';
import { TwoFaScreenTranslations } from '../../utils/get2faTranslations';

type TwoFaInputStatus = 'invalid' | 'valid' | 'incomplete';

const isInputValid = (value: string): boolean => {
  // Check if the length is at least 6 characters
  if (value.length < 6) {
    return false;
  }

  // Check if the string contains only letters (uppercase or lowercase) and numbers
  return /^[A-Za-z0-9]+$/.test(value);
};

const ResetTwoFaMethodRecoveryCode: FC<{
  onContinue: DeleteTwoFaOnContinue;
  dismissLoading?: boolean;
  translations: TwoFaScreenTranslations;
  bindICodeResponse: (response: ICodeResponseType) => void;
}> = ({ onContinue, dismissLoading, bindICodeResponse, translations }) => {
  const [inputStatus, setInputStatus] =
    useState<TwoFaInputStatus>('incomplete');

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const { URLforCustomerSupport } = getAppConfig();

  const [inputValue, setInputValue] = useState('');

  const [localLoading, setLocalLoading] = useState(false);

  const anyLoading = dismissLoading || localLoading;

  const onSubmitRecoveryCode = async (code: string) => {
    setLocalLoading(true);

    try {
      const resp = await submitResetTwoFaAppCode(code);

      if (resp.errors || resp.message === 'invalid code') {
        setInputStatus('invalid');

        processErrors(resp.errors);

        setLocalLoading(false);

        return;
      } else {
        setLocalLoading(false);

        onContinue();
      }

      bindICodeResponse(resp);
    } catch (_error) {
      setLocalLoading(false);

      setInputStatus('invalid');

      return;
    }
  };

  const onInputValueChange = (value: string) => {
    setInputValue(value);

    setInputStatus(isInputValid(value) ? 'valid' : 'incomplete');
  };

  return (
    <>
      <TwoFaSectionTitle title={translations.reset2faRecoveryTitle} />
      <TwoFaSectionDescription
        textContent={translations.reset2faRecoveryDescription}
      />
      <div>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => onInputValueChange(e.target.value)}
          placeholder={translations.generalRecoveryCode}
          className={`${css({
            width: '100%',
            maxWidth: '340px',
            margin: '0 auto',
            border: '1px solid',
            borderRadius: '8px',
            px: { base: 'extra_small', lg: 'small' },
            height: 'extra_large_4',
            lineHeight: 'extra_large_4',
            fontSize: 'headline.small_medium',
            ...(inputStatus === 'invalid'
              ? { borderColor: 'negative.stroke_primary' }
              : { borderColor: 'neutrals.stroke_primary' }),
          })}`}
        />
        {inputStatus === 'invalid' && (
          <p
            className={css({
              color: 'negative.text_primary',
              textAlign: 'center',
              py: 'extra_small_3',
              fontSize: 'caption.small',
            })}
          >
            {translations.reset2faInvalidCode}
          </p>
        )}
      </div>

      <TwoFaSectionButtonsWrapper>
        {URLforCustomerSupport && (
          <HyperLinkButtonBase
            visual="ghost"
            to={URLforCustomerSupport}
            className={css({
              textDecoration: 'underline',
            })}
          >
            {translations.reset2faOptionHelpdesk}
          </HyperLinkButtonBase>
        )}
        <ButtonBase
          isLoading={anyLoading}
          onClick={() => onSubmitRecoveryCode(inputValue)}
          visual="primary"
          size="large"
          fullWidth
          disabled={anyLoading ? true : inputStatus !== 'valid'}
        >
          {translations.reset2faButton}
        </ButtonBase>
      </TwoFaSectionButtonsWrapper>
    </>
  );
};

export { ResetTwoFaMethodRecoveryCode };
